import { useQuery } from "@tanstack/react-query";
import { auth } from "../../../components/Auth/Auth.jsx";

/**
 * Fetches all controllers for a specific shop.
 */
export const getShopControllers = async (shopId) => {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No authenticated user found");
  }

  if (!shopId) {
    throw new Error("Shop ID is required");
  }

  const token = await user.getIdToken();

  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/v2/shop/controllers?shopId=${encodeURIComponent(shopId)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Unauthorized!");
    }

    throw new Error("Network response was not ok");
  }

  return response.json();
};

/**
 * Fetches controllers from multiple shops in small batches to avoid API rate limits.
 */
export const getAllShopControllers = async (shopIds = []) => {
  const results = [];
  const batchSize = 4;

  for (let index = 0; index < shopIds.length; index += batchSize) {
    const batch = shopIds.slice(index, index + batchSize);

    const batchResults = await Promise.all(
      batch.map(async (shopId) => {
        if (!shopId) {
          return null;
        }

        try {
          const shopData = await getShopControllers(shopId);

          return {
            ...shopData,
            shopId,
          };
        } catch (error) {
          console.error("Failed to load shop controllers:", shopId, error);
          return null;
        }
      }),
    );

    results.push(...batchResults.filter(Boolean));
  }

  return results;
};

/**
 * Loads the controllers for a shop.
 * Additional React Query options can be provided through the options parameter.
 */
export const UseShopControllersData = (shopId, options = {}) => {
  const query = useQuery({
    queryKey: ["ShopControllers", shopId],
    queryFn: () => getShopControllers(shopId),

    // The query only runs when a valid shop ID exists.
    enabled: !!shopId,

    // Allows the component to provide options such as refetchInterval.
    ...options,
  });

  return {
    data: query.data,
    isPending: query.isPending,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
};
